import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { getStoredToken, getStoredUser, getStoredSubAccountContext } from "../utils/helpers";
import { syncCompanionSession, clearCompanionSession } from "../services/uploadService";
import { getMySubAccountContext } from "../services/subAccountService";

const AuthContext = createContext(null);
const SESSION_INACTIVITY_TIMEOUT_MS = 2 * 60 * 60 * 1000;
const LAST_ACTIVITY_STORAGE_KEY = "ps_last_activity_at";
const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "pointerdown",
  "focus",
];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getStoredUser);
  const [token, setToken] = useState(getStoredToken);
  const [subAccountContext, setSubAccountContext] = useState(getStoredSubAccountContext);
  const idleTimerRef = useRef(null);

  /** Fetch + persist Sub-Account context (isSubAccount/ownerBusinessName/permissions)
   *  so the panel knows whether the logged-in user is acting on behalf of a Business
   *  Account. Safe to call for any logged-in user — returns IsSubAccount: false for
   *  regular Business Accounts. */
  const refreshSubAccountContext = useCallback(async () => {
    try {
      const res = await getMySubAccountContext();
      const ctx = res.data?.Result || null;
      localStorage.setItem("subAccountContext", JSON.stringify(ctx));
      setSubAccountContext(ctx);
      return ctx;
    } catch {
      return null;
    }
  }, []);

  const login = useCallback((userData, authToken) => {
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("token", authToken);
    localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(Date.now()));
    setUser(userData);
    setToken(authToken);
    syncCompanionSession().catch(() => {});
    refreshSubAccountContext();
  }, [refreshSubAccountContext]);

  /** Persist + apply freshly-saved profile/business data without touching the token or re-syncing the companion session. */
  const updateUser = useCallback((userData) => {
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
  }, []);

  /** Rotate the stored JWT after a password change (mobile's onRefreshToken flow). */
  const updateToken = useCallback((authToken) => {
    localStorage.setItem("token", authToken);
    setToken(authToken);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
    localStorage.removeItem("subAccountContext");
    setUser(null);
    setToken(null);
    setSubAccountContext(null);
    clearCompanionSession().catch(() => {});
  }, []);

  // On app load with an existing session (page refresh), make sure Sub-Account
  // context is present at least once — login() already handles the fresh-login case.
  useEffect(() => {
    if (token && !subAccountContext) {
      refreshSubAccountContext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!token) {
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
      return;
    }

    let lastActivityAt = Number(localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY));
    if (!Number.isFinite(lastActivityAt) || lastActivityAt <= 0) {
      lastActivityAt = Date.now();
      localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(lastActivityAt));
    }

    const scheduleIdleLogout = () => {
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);

      const elapsedMs = Date.now() - lastActivityAt;
      const remainingMs = SESSION_INACTIVITY_TIMEOUT_MS - elapsedMs;

      if (remainingMs <= 0) {
        logout();
        return;
      }

      idleTimerRef.current = window.setTimeout(logout, remainingMs);
    };

    const recordActivity = () => {
      if (Date.now() - lastActivityAt >= SESSION_INACTIVITY_TIMEOUT_MS) {
        logout();
        return;
      }

      lastActivityAt = Date.now();
      localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(lastActivityAt));
      scheduleIdleLogout();
    };

    scheduleIdleLogout();
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, recordActivity, { passive: true });
    });

    return () => {
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, recordActivity);
      });
    };
  }, [logout, token]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        updateUser,
        updateToken,
        subAccountContext,
        refreshSubAccountContext,
        isSubAccount: Boolean(subAccountContext?.IsSubAccount),
        subAccountPermissions: subAccountContext?.Permissions || null,
        ownerBusinessName: subAccountContext?.OwnerBusinessName || null,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
